import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { CalendarDays, User, Users, Loader2, CheckCircle2, ChevronLeft, ChevronRight, MailCheck } from 'lucide-react';
import { format, addDays, isToday } from 'date-fns';
import { it } from 'date-fns/locale';

const pubApi = axios.create({ baseURL: '/api' });

const MOTIVI = [
  'Polizza azienda', 'Piano previdenziale complementare', 'PIP',
  'Finanziamento CQP/CQS', 'Finanziamento PP', 'Polizza Casa',
  'Assicurazione salute/Infortuni/Vita', 'Consulenza generica', 'Altro'
];

export default function PrenotaPubblica() {
  const [info, setInfo] = useState(null);
  const [persona, setPersona] = useState('any');
  const [dayOffset, setDayOffset] = useState(0);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [slots, setSlots] = useState([]);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState('');
  const [form, setForm] = useState({ nome: '', telefono: '', email: '', motivo: '', note: '' });
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    pubApi.get('/public/booking/info').then(r => setInfo(r.data)).catch(() => setError('Servizio non disponibile'));
  }, []);

  const dateStr = format(selectedDate, 'yyyy-MM-dd');

  const loadSlots = useCallback(async () => {
    setSlotsLoading(true);
    setSelectedSlot('');
    try {
      const r = await pubApi.get(`/public/booking/slots?data=${dateStr}&user_id=${persona}`);
      setSlots(r.data.slots || []);
    } catch { setSlots([]); }
    finally { setSlotsLoading(false); }
  }, [dateStr, persona]);

  useEffect(() => { loadSlots(); }, [loadSlots]);

  const days = Array.from({ length: 7 }, (_, i) => addDays(new Date(), dayOffset * 7 + i));

  const submit = async () => {
    if (!form.nome.trim() || !form.telefono.trim() || !form.email.trim()) {
      setError('Nome, telefono ed email sono obbligatori'); return;
    }
    if (!selectedSlot) { setError('Seleziona un orario'); return; }
    setError(''); setSending(true);
    try {
      await pubApi.post('/public/booking/richiedi', {
        ...form, data: dateStr, ora: selectedSlot, user_id: persona,
        origin: window.location.origin,
      });
      setDone(true);
    } catch (e) {
      setError(e.response?.data?.detail || 'Errore durante la richiesta. Riprova.');
    } finally { setSending(false); }
  };

  if (done) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-8 max-w-md text-center" data-testid="prenota-done">
          <div className="w-16 h-16 rounded-full bg-emerald-50 flex items-center justify-center mx-auto mb-4">
            <MailCheck size={28} className="text-emerald-600" />
          </div>
          <h1 className="text-xl font-bold text-slate-900 mb-2">Controlla la tua email</h1>
          <p className="text-sm text-slate-500">
            Ti abbiamo inviato una email a <b>{form.email}</b>.<br />
            Clicca sul link per confermare la prenotazione del <b>{format(selectedDate, 'dd MMMM', { locale: it })}</b> alle <b>{selectedSlot}</b>.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 py-8 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-12 h-12 rounded-xl bg-slate-800 flex items-center justify-center mx-auto mb-3">
            <CalendarDays size={22} className="text-amber-400" />
          </div>
          <h1 className="text-2xl font-bold text-slate-900">Prenota un appuntamento</h1>
          <p className="text-sm text-slate-500 mt-1">{info?.agenzia || "F.lli Schilirò Assicurazioni"}</p>
        </div>

        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5 sm:p-6 space-y-6">
          {/* Step 1: persona */}
          <div>
            <p className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">1. Con chi vuoi parlare?</p>
            <div className="flex flex-wrap gap-2">
              <button onClick={() => setPersona('any')}
                className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium border transition-colors ${persona === 'any' ? 'bg-slate-800 text-white border-slate-800' : 'bg-white text-slate-600 border-slate-200 hover:border-slate-400'}`}
                data-testid="persona-any">
                <Users size={14} /> Chiunque sia disponibile
              </button>
              {(info?.team || []).map(m => (
                <button key={m.id} onClick={() => setPersona(m.id)}
                  className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium border transition-colors ${persona === m.id ? 'bg-slate-800 text-white border-slate-800' : 'bg-white text-slate-600 border-slate-200 hover:border-slate-400'}`}
                  data-testid={`persona-${m.id}`}>
                  <User size={14} /> {m.nome}
                </button>
              ))}
            </div>
          </div>

          {/* Step 2: data */}
          <div>
            <p className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">2. Scegli il giorno</p>
            <div className="flex items-center gap-1.5">
              <button onClick={() => setDayOffset(o => Math.max(0, o - 1))} disabled={dayOffset === 0}
                className="p-2 rounded-lg border border-slate-200 text-slate-400 disabled:opacity-30 hover:bg-slate-50"><ChevronLeft size={15} /></button>
              <div className="flex-1 grid grid-cols-7 gap-1">
                {days.map(d => {
                  const sel = format(d, 'yyyy-MM-dd') === dateStr;
                  return (
                    <button key={d.toISOString()} onClick={() => setSelectedDate(d)}
                      className={`flex flex-col items-center py-2 rounded-lg text-xs transition-colors ${sel ? 'bg-slate-800 text-white font-bold' : isToday(d) ? 'bg-amber-50 text-amber-700 border border-amber-200' : 'text-slate-600 hover:bg-slate-100'}`}
                      data-testid={`giorno-${format(d, 'yyyy-MM-dd')}`}>
                      <span className="capitalize text-[10px]">{format(d, 'EEE', { locale: it })}</span>
                      <span className="text-sm font-semibold">{d.getDate()}</span>
                    </button>
                  );
                })}
              </div>
              <button onClick={() => setDayOffset(o => o + 1)}
                className="p-2 rounded-lg border border-slate-200 text-slate-400 hover:bg-slate-50"><ChevronRight size={15} /></button>
            </div>
          </div>

          {/* Step 3: slot */}
          <div>
            <p className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">3. Scegli l'orario</p>
            {slotsLoading ? (
              <div className="py-6 text-center"><Loader2 className="animate-spin mx-auto text-slate-300" size={18} /></div>
            ) : slots.length === 0 ? (
              <p className="text-sm text-slate-400 py-4 text-center bg-slate-50 rounded-lg">Nessun orario disponibile per questo giorno</p>
            ) : (
              <div className="grid grid-cols-4 sm:grid-cols-6 gap-1.5" data-testid="slots-grid">
                {slots.map(s => (
                  <button key={s} onClick={() => setSelectedSlot(s)}
                    className={`py-2 rounded-lg text-sm font-mono font-semibold border transition-colors ${selectedSlot === s ? 'bg-emerald-600 text-white border-emerald-600' : 'bg-emerald-50 text-emerald-700 border-emerald-100 hover:border-emerald-400'}`}
                    data-testid={`slot-${s}`}>
                    {s}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Step 4: dati */}
          <div>
            <p className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">4. I tuoi dati</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <input value={form.nome} onChange={e => setForm(f => ({ ...f, nome: e.target.value }))} placeholder="Nome e cognome *"
                className="px-3 py-2.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-slate-300" data-testid="prenota-nome" />
              <input value={form.telefono} onChange={e => setForm(f => ({ ...f, telefono: e.target.value }))} placeholder="Telefono *"
                className="px-3 py-2.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-slate-300" data-testid="prenota-telefono" />
              <input value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} placeholder="Email *" type="email"
                className="px-3 py-2.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-slate-300" data-testid="prenota-email" />
              <select value={form.motivo} onChange={e => setForm(f => ({ ...f, motivo: e.target.value }))}
                className="px-3 py-2.5 border border-slate-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-slate-300" data-testid="prenota-motivo">
                <option value="">Motivo dell'appuntamento</option>
                {MOTIVI.map(m => <option key={m} value={m}>{m}</option>)}
              </select>
            </div>
            <textarea value={form.note} onChange={e => setForm(f => ({ ...f, note: e.target.value }))} placeholder="Note (facoltative)" rows={2}
              className="w-full mt-3 px-3 py-2.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-slate-300" data-testid="prenota-note" />
          </div>

          {error && <p className="text-sm text-red-600 bg-red-50 rounded-lg px-3 py-2" data-testid="prenota-error">{error}</p>}

          <button onClick={submit} disabled={sending || !selectedSlot}
            className="w-full py-3 rounded-xl bg-slate-800 hover:bg-slate-700 text-white font-semibold text-sm disabled:opacity-40 transition-colors flex items-center justify-center gap-2"
            data-testid="prenota-submit">
            {sending ? <Loader2 size={16} className="animate-spin" /> : <CheckCircle2 size={16} />}
            {selectedSlot ? `Richiedi appuntamento — ${format(selectedDate, 'dd MMM', { locale: it })} ore ${selectedSlot}` : 'Seleziona un orario'}
          </button>
          <p className="text-[11px] text-slate-400 text-center">Riceverai una email per confermare la prenotazione.</p>
        </div>
      </div>
    </div>
  );
}
